import { type ParsedLocation, type RouterEvents } from '@tanstack/react-router';

import casinoConfig from '@dcentralexpedientedigital/config';
import { isBlocks } from '@dcentralexpedientedigital/fe-casino/src/utils/is-blocks';
import { useIntlStore } from '@dcentralexpedientedigital/fe-shared/stores/intl-store';
import type { IStaticBlocksData } from '@dcentralexpedientedigital/fe-shared/types/config.interface';
import type { User } from 'oidc-client-ts';

import type { router as ourRouter } from '../routes';

export interface UrlSearchSchema {
    modal: string;
}

interface LiveChatConfig {
    scriptSourceUrl: string;
}

let liveChatIntegrated = false;

// Functions to update Zoho visitor info - called by AuthEventManager component
export const updateZohoVisitorInfo = (user: User | null) => {
    if (typeof window !== 'undefined' && window.$zoho?.salesiq?.visitor) {
        const setField = (key: 'id' | 'email' | 'name', value: string | null | undefined) => {
            const fn = window.$zoho?.salesiq?.visitor?.[key];
            if (typeof fn === 'function') {
                fn(value ?? '');
            }
        };

        if (user?.profile) {
            setField('id', user.profile.sub);
            setField('email', user.profile.email);
            setField('name', user.profile.given_name);
        } else {
            setField('id', null);
            setField('email', null);
            setField('name', null);
        }
    }
};

export const resetZohoUserInfo = () => {
    if (typeof window !== 'undefined' && window.$zoho?.salesiq?.visitor) {
        const setField = (key: 'id' | 'email' | 'name', value: string | null | undefined) => {
            const fn = window.$zoho?.salesiq?.visitor?.[key];
            if (typeof fn === 'function') {
                fn(value ?? '');
            }
        };

        setField('id', null);
        setField('email', null);
        setField('name', null);
    }
};

export const initializeLiveChatHandler = (liveChat: LiveChatConfig, router: typeof ourRouter) => {
    const scripts: HTMLScriptElement[] = [];
    let isReady = false;
    let shouldOpenChat = false;

    const onOpenChat = () => {
        window.$zoho?.salesiq?.floatbutton?.visible('hide');
        window.$zoho?.salesiq?.floatwindow?.visible('show');
    };

    const openChat = () => {
        if (isReady) {
            onOpenChat();
            return;
        }

        // Let the script load and then open the chat
        shouldOpenChat = true;
    };

    const addScripts = () => {
        if (liveChatIntegrated) {
            return;
        }

        const firstScript = document.createElement('script');
        firstScript.innerHTML =
            'window.$zoho=window.$zoho || {}; $zoho.salesiq=$zoho.salesiq || {ready:function(){}}';
        document.body.appendChild(firstScript);
        scripts.push(firstScript);

        const secondScript = document.createElement('script');
        secondScript.src = liveChat.scriptSourceUrl;
        secondScript.defer = true;
        secondScript.onload = () => {
            if (window.$zoho?.salesiq) {
                window.$zoho.salesiq.ready = () => {
                    if (typeof window.$zoho?.salesiq?.language === 'function') {
                        window.$zoho.salesiq.language(useIntlStore.getState().locale);
                    }

                    isReady = true;

                    window.$zoho?.salesiq?.chat?.agentMessage(() => {
                        window.$zoho?.salesiq?.floatwindow?.visible('show');
                    });

                    if (shouldOpenChat) {
                        onOpenChat();
                    } else {
                        window.$zoho?.salesiq?.floatbutton?.visible('hide');
                    }

                    if (window.$zoho?.salesiq?.floatwindow?.close) {
                        window.$zoho?.salesiq?.floatwindow?.close(() => {
                            window.$zoho?.salesiq?.reset();
                        });
                    }
                };
            }
        };
        document.body.appendChild(secondScript);
        scripts.push(secondScript);

        liveChatIntegrated = true;
    };

    addScripts();

    const setField = (key: 'id' | 'email' | 'name', value: string | null | undefined) => {
        const fn = window.$zoho?.salesiq?.visitor?.[key];
        if (typeof fn === 'function') fn(value ?? '');
    };

    const resetValues = () => {
        // Note: Currently zoho essentialy ignores null/empty values
        // however, have left this here for when it does support it.
        setField('id', null);
        setField('email', null);
        setField('name', null);
    };

    const getRoute = (
        pathname: string,
        search: Record<string, string> | UrlSearchSchema,
    ): IStaticBlocksData | undefined => {
        const matchedRoutes = router.matchRoutes(pathname, search);
        return matchedRoutes.find(({ staticData }: { staticData: unknown }) => isBlocks(staticData))
            ?.staticData as IStaticBlocksData;
    };

    const handleOnResolved = (event: RouterEvents['onBeforeLoad']) => {
        const { toLocation, fromLocation } = event;
        const { pathname: toPath, search } = toLocation as ParsedLocation<UrlSearchSchema>;
        const route = getRoute(toPath, search);

        if (route?.id === casinoConfig.liveChat.routeId) {
            router.navigate({
                to: fromLocation?.pathname ?? '/',
                search: fromLocation?.search,
                replace: true,
            });

            openChat();
        }
    };

    const unsubscribe = router.subscribe('onBeforeLoad', handleOnResolved);

    window.addEventListener('beforeunload', resetValues);

    return {
        cleanup: () => {
            unsubscribe();
            window.removeEventListener('beforeunload', resetValues);
        },
    };
};
